/**
 * `/dynamics/investigate` — the F&O Performance Console's per-user layer
 * (concept §7 L2; D1 go, D7 reason-gated and audit-logged, on demand).
 *
 * Flow: the reason gate asks once per session → search a person → the page
 * navigates to `?mail=…&lookback=…` → header, coverage meter, the Machine
 * section, the two-chart timeline, the slow-interactions grid → a row or a slow
 * bar opens the §5 record drawer at `?activity=…`. Everything is in the URL, so
 * an investigation is a link; the reason is not (it is session state the backend
 * logs).
 *
 * The **Machine section** under the header is the one panel on this page that
 * reads Defender rather than Log Analytics, and it is collapsed by default: the
 * lookup writes an audit row before Defender is asked, so it must not fire for
 * every person anyone opens. See `machine-section.tsx` for why every one of its
 * states — including the `ThreatHunting.Read.All` refusal that is the expected
 * answer today — reads as information rather than as an empty device list.
 *
 * The **user-report queue** used to sit at the bottom of this page and now has
 * its own screen, `/dynamics/reports`. It is where an investigation usually
 * starts, so having it below the per-person view put the first step last; and it
 * is a work queue with its own window, status filter and selection, which is a
 * screen rather than a panel. A card near the top links across to it.
 *
 * Navigation is the rail's, not this page's: the six Dynamics screens are
 * registry entries in `components/nav-sections.ts`, so the hand-built
 * `DynamicsSubNav` that used to sit under the header is gone.
 *
 * The board's worst-forms and worst-sites rows land here with `?form=` or
 * `?site=`, which pre-filter the grid once a person is chosen. There is no
 * "all users at this site" list on purpose — a site view would show named
 * people before any reason was given (concept §7 leaves it anonymised until
 * then; it is not built).
 *
 * States are explicit: a missing reason locks the page behind its dialog, a
 * 403 says this sign-in is not on the investigator list (`NOVA_FO_INVESTIGATORS`
 * — every pilot user of the browser extension is a signed-in NOVA user, so the
 * list is the gate, not the sign-in), a 400 says the backend refused the
 * reason, a 404 says the mail is not a known person, an unreadable workspace
 * shows the access panel, and a person with no network rows sees "not visible"
 * rather than an empty chart.
 */

import { Icon, KeyRound, MessageSquareWarning, UserSearch as UserSearchIcon } from "@norican/icons";
import {
  Alert,
  Badge,
  type BreadcrumbItem,
  Breadcrumbs,
  Button,
  Card,
  Chip,
  DescriptionList,
  EmptyState,
  Freshness,
  PageHeader,
  Skeleton,
  Typography,
} from "@norican/react";
import { type ReactNode, useState } from "react";
import { Link } from "react-router";

import { type FoConsoleCandidate, useFoConsoleUser } from "@/lib/api";
import { drillTo, useDrawerParam, useDrillNavigate, useDrillParams } from "@/lib/drill";

import { FoAccessPanel, FoErrorsAlert, isPanelOk } from "./access-panel";
import { AuditSection } from "./audit-section";
import { CoverageMeter } from "./console-chips";
import { apiErrorDetail, apiErrorStatus, formatCoverage, lookbackOption } from "./console-format";
import { BoardCrossLink } from "./cross-link";
import { LookbackRow, useLookbackParam } from "./lookback-row";
import { MachineSection } from "./machine-section";
import { PanelCard } from "./panel-card";
import {
  ACTIVITY_PARAM,
  DYNAMICS_INVESTIGATE_PATH,
  DYNAMICS_PATH,
  DYNAMICS_REPORTS_PATH,
  FORM_PARAM,
  LOOKBACK_PARAM,
  MAIL_PARAM,
  SITE_PARAM,
} from "./params";
import { ReasonControl, ReasonDialog, useInvestigationReason } from "./reason-gate";
import { RecordDrawer } from "./record-drawer";
import { SlowGrid } from "./slow-grid";
import { formatCount } from "./status";
import { ConsoleToasts } from "./toast-stack";
import { UserSearch } from "./user-search";
import { UserTimeline } from "./user-timeline";

/** One line on what lives on `/dynamics/reports`, so the door says where it goes. */
const REPORT_QUEUE_CROSS_LINK =
  'The people who pressed "this app is slow right now" are a queue of their own, with its own window and triage states. Opening one hands its matched F&O load straight back to a record here.';

const BREADCRUMBS: BreadcrumbItem[] = [
  {
    label: "Dynamics",
    render: ({ className, children }: { className: string; children: ReactNode }) => (
      <Link to={DYNAMICS_PATH} className={className}>
        {children}
      </Link>
    ),
  },
  { label: "Investigate", current: true },
];

export function DynamicsInvestigatePage() {
  return (
    <ConsoleToasts>
      <InvestigatePageBody />
    </ConsoleToasts>
  );
}

function InvestigatePageBody() {
  const gate = useInvestigationReason();
  const [dialogOpen, setDialogOpen] = useState(gate.reason == null);
  const lookback = useLookbackParam();
  const drill = useDrillParams(MAIL_PARAM, FORM_PARAM, SITE_PARAM);
  const drawer = useDrawerParam(ACTIVITY_PARAM);
  const navigateDrill = useDrillNavigate();

  const mail = drill.params[MAIL_PARAM];
  const formFilter = drill.params[FORM_PARAM];
  const siteFilter = drill.params[SITE_PARAM];

  const user = useFoConsoleUser(mail, lookback, gate.reason);
  const view = user.data;
  const staleAfterMs = view ? view.cache_ttl_sec * 2000 : undefined;
  const status = apiErrorStatus(user.error);

  const pick = (candidate: FoConsoleCandidate) => {
    navigateDrill(DYNAMICS_INVESTIGATE_PATH, {
      [MAIL_PARAM]: candidate.mail,
      [LOOKBACK_PARAM]: lookback,
      [FORM_PARAM]: formFilter,
      [SITE_PARAM]: siteFilter,
    });
  };

  const filteredSlow = (view?.slow ?? []).filter(
    (row) =>
      (!formFilter || row.form === formFilter) && (!siteFilter || (row.site ?? "") === siteFilter),
  );

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <Breadcrumbs items={BREADCRUMBS} />
      <PageHeader
        title="Investigate F&O slowness for one person"
        subtitle="Which layer made this user's session slow — endpoint, network path, F&O — one record at a time. On demand, named investigators only, reason-gated, audit-logged."
        actions={
          <div className="flex flex-wrap items-center gap-4">
            <ReasonControl reason={gate.reason} onChange={() => setDialogOpen(true)} />
            {view?.collected_at && (
              <Freshness
                label="Workspace read"
                since={view.collected_at}
                staleAfter={staleAfterMs}
                pulse={user.isFetching}
              />
            )}
          </div>
        }
      />

      <ReasonDialog
        key={gate.reason ?? "none"}
        open={dialogOpen}
        initialReason={gate.reason}
        onCancel={() => setDialogOpen(false)}
        onSubmit={(reason) => {
          if (gate.setReason(reason)) {
            setDialogOpen(false);
          }
        }}
      />

      {gate.reason == null && (
        <Card variant="outline" accent="left" accentTone="warning">
          <Card.Body>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <Icon icon={KeyRound} size="md" aria-hidden="true" />
                <div className="flex flex-col gap-1">
                  <Typography variant="heading-4" as="h2">
                    Per-user views need a reason
                  </Typography>
                  <Typography variant="body-sm" tone="muted">
                    A ticket or report id, or a short free-text reason. It is sent with every
                    per-user call, logged with your name and the person you look at, and kept for
                    this browser session only (D7).
                  </Typography>
                </div>
              </div>
              <Button onClick={() => setDialogOpen(true)}>Enter a reason</Button>
            </div>
          </Card.Body>
        </Card>
      )}

      <LookbackRow scopes="this person's view" />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <PanelCard
            title="Who"
            caption={
              formFilter || siteFilter
                ? `arrived from the board filtered to ${formFilter ? `form ${formFilter}` : ""}${
                    formFilter && siteFilter ? " and " : ""
                  }${siteFilter ? `site ${siteFilter}` : ""} — pick the person to look at`
                : "name, mail or F&O alias"
            }
          >
            <div className="flex flex-col gap-3">
              <UserSearch onPick={pick} disabled={gate.reason == null} />
              {(formFilter || siteFilter) && (
                <div className="flex flex-wrap items-center gap-2">
                  <Typography variant="caption" tone="muted">
                    Grid filter:
                  </Typography>
                  {formFilter && (
                    <Chip
                      size="sm"
                      onRemove={() => drill.set({ [FORM_PARAM]: null })}
                      removeLabel="Remove the form filter"
                    >
                      form {formFilter}
                    </Chip>
                  )}
                  {siteFilter && (
                    <Chip
                      size="sm"
                      onRemove={() => drill.set({ [SITE_PARAM]: null })}
                      removeLabel="Remove the site filter"
                    >
                      site {siteFilter}
                    </Chip>
                  )}
                </div>
              )}
            </div>
          </PanelCard>
        </div>
        {/* Where an investigation usually starts. The queue has its own screen
            now (`/dynamics/reports`): its own window, its own triage states, its
            own selection. So this is a door to it, never a copy of it. */}
        <BoardCrossLink
          title="Reports from users"
          caption={REPORT_QUEUE_CROSS_LINK}
          to={DYNAMICS_REPORTS_PATH}
          cta="Open the report queue"
          icon={MessageSquareWarning}
        />
      </div>

      {!mail && (
        <EmptyState
          icon={<Icon icon={UserSearchIcon} size="lg" aria-hidden="true" />}
          title="No person selected"
          description="Search above. Shared shop-floor and system accounts are never attributable to a person and do not appear."
        />
      )}

      {mail && gate.reason == null && (
        <Alert tone="warning" title={`${mail} is selected, but nothing has been fetched`}>
          Enter a reason to load this person&apos;s view. Nothing is requested without one.
        </Alert>
      )}

      {mail && user.error && status === 403 && (
        <Alert tone="warning" title="This sign-in is not on the investigator list">
          {apiErrorDetail(user.error) ??
            "Per-user views are open to the investigators named in NOVA's configuration only. The board's aggregates stay available."}
        </Alert>
      )}
      {mail && user.error && status === 400 && (
        <Alert tone="warning" title="The backend refused the call: reason missing or too short">
          {apiErrorDetail(user.error) ??
            "Every per-user call carries a reason of at least four characters. Change it above and the view reloads."}
        </Alert>
      )}
      {mail && user.error && status === 404 && (
        <Alert tone="neutral" title={`${mail} is not a known person`}>
          {apiErrorDetail(user.error) ??
            "The identity map has no enabled Entra user with this mail. Shared and system accounts are excluded by design; a leaver drops out when the daily identity job runs."}
        </Alert>
      )}
      {mail && user.error && status !== 400 && status !== 403 && status !== 404 && (
        <Alert tone="danger" title="Couldn't load this person's view">
          {status === 0
            ? "The NOVA API did not respond. This is a NOVA problem, not an Azure one."
            : (apiErrorDetail(user.error) ?? `The NOVA API answered ${status ?? "with an error"}.`)}
        </Alert>
      )}

      {mail && gate.reason != null && user.isPending && !view && !user.error && (
        <div className="flex flex-col gap-4" aria-busy="true">
          <Skeleton height="6rem" />
          <Skeleton height="18rem" />
          <Skeleton height="14rem" />
        </div>
      )}

      {view && !isPanelOk(view) && (
        <FoAccessPanel
          panel={`Per-user view for ${view.identity?.display_name ?? mail ?? "this person"}`}
          state={view}
        />
      )}

      {view && isPanelOk(view) && (
        <>
          {view.errors.length > 0 && <FoErrorsAlert errors={view.errors} />}

          <Card variant="outline">
            <Card.Body>
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="lg:col-span-2">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <Typography variant="heading-3" as="h2">
                      {view.identity?.display_name ?? mail}
                    </Typography>
                    <Badge tone="neutral" variant="outline" size="xs">
                      {/* A missing identity block means the alias join did not
                          resolve — say so rather than showing a blank kind. */}
                      {view.identity?.account_kind ?? "identity not resolved"}
                    </Badge>
                  </div>
                  <DescriptionList
                    columns={2}
                    className="mt-3"
                    items={[
                      {
                        label: "Mail",
                        value: <span className="break-all">{view.identity?.mail ?? mail}</span>,
                      },
                      { label: "F&O alias", value: view.identity?.alias ?? "—" },
                      { label: "Lookback", value: lookbackOption(view.lookback).caption },
                      { label: "Page views", value: formatCount(view.timeline.length) },
                      { label: "Slow", value: formatCount(view.slow.length) },
                      { label: "Exceptions", value: formatCount(view.exceptions.length) },
                    ]}
                  />
                </div>
                <CoverageMeter
                  coverage={view.coverage}
                  label="Network coverage for this person"
                  caption={`15-minute buckets with a Cato row ÷ buckets with page views: ${formatCoverage(
                    view.coverage,
                  )}. Without a row the path is unknown, not fine.`}
                />
              </div>
            </Card.Body>
          </Card>

          {/* What the machines *are*, from Defender — the half the browser
              cannot report. Collapsed, so it costs no audited request until
              someone actually asks the question. */}
          <MachineSection mail={mail} reason={gate.reason} />

          <UserTimeline
            view={view}
            lookback={lookback}
            staleAfterMs={staleAfterMs ?? 0}
            onOpenRecord={(activityId) => drawer.open(activityId)}
          />

          <PanelCard
            title="Slow interactions"
            caption={`${formatCount(filteredSlow.length)}${
              filteredSlow.length !== view.slow.length ? ` of ${formatCount(view.slow.length)}` : ""
            } · ${lookbackOption(view.lookback).caption} · click a time for the record`}
            padding={filteredSlow.length === 0 ? "md" : "none"}
          >
            <SlowGrid
              rows={filteredSlow}
              onOpen={(activityId) => drawer.open(activityId)}
              ariaLabel={`Slow interactions for ${view.identity?.display_name ?? mail}`}
            />
          </PanelCard>
        </>
      )}

      <RecordDrawer
        activityId={drawer.value}
        reason={gate.reason}
        onClose={drawer.close}
        breadcrumbs={[
          { label: "Dynamics", to: DYNAMICS_PATH },
          {
            label: "Investigate",
            to: drillTo(DYNAMICS_INVESTIGATE_PATH, {
              [MAIL_PARAM]: mail,
              [LOOKBACK_PARAM]: lookback,
            }),
          },
        ]}
      />

      <AuditSection />
    </main>
  );
}
